import type { PostUserPermissionsPayload } from "@/core/types/(api)/permissions";
import { useOptimisticMutation } from "@/lib/hooks/useOptimisticMutation";
import type { UserPermissions, UUID } from "@/core/types";

import { permissionsKeys } from "../keyFactory";
import { updateUserPermissions } from "../mutationFunctions/updateUserPermissions";

type UsePermissionsMutationProps<TPayload extends PostUserPermissionsPayload> = {
  onSuccess?: (data: UserPermissions, payload: TPayload) => void;
  optimisticStateResolver: (old: UserPermissions, payload: TPayload) => UserPermissions | undefined;
  userId: UUID;
  userName: string;
};

// Internal building block — not meant to be used directly by components. Each permission
// group gets its own lean hook (useUpdateCosignPermission, useUpdateLcswPermission, ...)
// that supplies only its `optimisticStateResolver` rule; this hook owns the permissions-specific
// query key / mutation function / error copy on top of the shared useOptimisticMutation plumbing.
export function usePermissionsMutation<TPayload extends PostUserPermissionsPayload>({
  onSuccess,
  optimisticStateResolver,
  userId,
  userName,
}: UsePermissionsMutationProps<TPayload>) {
  return useOptimisticMutation<UserPermissions, TPayload>({
    getErrorMessage: () => `Couldn't update permissions for ${userName}. Please try again.`,
    mutationFn: (payload) => updateUserPermissions(userId, payload),
    mutationKey: permissionsKeys.updateUserPermissions(userId),
    onSuccess,
    optimisticStateResolver,
    queryKey: permissionsKeys.userPermissions(userId),
  });
}
